import { NormalizedTrackingProduct } from "./itemDataTransformer"

interface ProductOriginData {
    item_list_id: string
    item_list_name: string
    index?: number
    list_hierarchy?: string
    promotion_id?: string
    promotion_name?: string
    timestamp: number
}

const STORAGE_KEY = "product_tracking_origins"
const EXPIRATION_HOURS = 24

/**
 * Generate a unique product key from transformed tracking data
 * Uses only item_id as the identifier
 */
export function generateProductKeyFromTrackingData(
    trackingData: NormalizedTrackingProduct
): string {
    return trackingData.item_id
}

/**
 * Store product origin tracking data in session storage
 */
export function storeProductOrigin(
    productKey: string,
    itemListId: string,
    itemListName: string,
    index?: number,
    listHierarchy?: string,
    promotionId?: string,
    promotionName?: string
): void {
    if (typeof window === "undefined") return

    try {
        const existingData = getStoredOrigins()

        const originData: ProductOriginData = {
            item_list_id: itemListId,
            item_list_name: itemListName,
            index,
            list_hierarchy: listHierarchy,
            promotion_id: promotionId,
            promotion_name: promotionName,
            timestamp: Date.now()
        }

        existingData[productKey] = originData

        // Clean up expired entries
        const cleanedData = cleanupExpiredEntries(existingData)

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cleanedData))
    } catch (error) {
        console.error("Failed to store product origin:", error)
    }
}

/**
 * Retrieve product origin tracking data from session storage
 */
export function getProductOrigin(productKey: string): ProductOriginData | null {
    if (typeof window === "undefined") return null

    try {
        const storedData = getStoredOrigins()
        const originData = storedData[productKey]

        if (originData && !isExpired(originData.timestamp)) {
            return originData
        }

        // Remove expired entry
        if (originData) {
            delete storedData[productKey]
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storedData))
        }

        return null
    } catch (error) {
        console.error("Failed to retrieve product origin:", error)
        return null
    }
}

/**
 * Get all stored origins (internal helper)
 */
function getStoredOrigins(): Record<string, ProductOriginData> {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY)
        return stored ? JSON.parse(stored) : {}
    } catch (error) {
        console.error("Failed to parse stored origins:", error)
        return {}
    }
}

/**
 * Check if a timestamp is expired
 */
function isExpired(timestamp: number): boolean {
    const expirationTime = timestamp + EXPIRATION_HOURS * 60 * 60 * 1000
    return Date.now() > expirationTime
}

/**
 * Clean up expired entries from storage
 */
function cleanupExpiredEntries(
    data: Record<string, ProductOriginData>
): Record<string, ProductOriginData> {
    const cleaned: Record<string, ProductOriginData> = {}

    for (const [key, value] of Object.entries(data)) {
        if (!isExpired(value.timestamp)) {
            cleaned[key] = value
        }
    }

    return cleaned
}

/**
 * Clear all stored product origins
 */
export function clearProductOrigins(): void {
    if (typeof window === "undefined") return

    try {
        sessionStorage.removeItem(STORAGE_KEY)
    } catch (error) {
        console.error("Failed to clear product origins:", error)
    }
}

/**
 * Get all active product origins (for debugging)
 */
export function getAllProductOrigins(): Record<string, ProductOriginData> {
    if (typeof window === "undefined") return {}

    try {
        const storedData = getStoredOrigins()
        return cleanupExpiredEntries(storedData)
    } catch (error) {
        console.error("Failed to get all product origins:", error)
        return {}
    }
}
